import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import lang from '../../../lib/language';
import { ENROLL_AUTH_REQUEST_TYPE } from '../../../lib/models/enroll-requests';
import { enrollAuth } from '../../../lib/store/auth';
import { calculateElapsedTime } from '../../../lib/util/DateTimeHelpers';
import { useCounter } from '../../../lib/util/hooks/TimeoutHooks';

import emailImage from '../../../assets/img/email_letter.png';
import IconClose from '../../../assets/img/icon-close.svg';
import { ClickableDiv, ClickableImage } from '../../index';

import styles from './VerifyEmailModal.module.scss';

type Props = {
  forceNoRender?: boolean;
  noModalDisplay?: boolean;
  handleCloseVerifyEmailModal: () => void;
  userEmail: string;
  largeText?: boolean;
};
const SECONDS = 60;

const VerifyEmailModal = ({
  handleCloseVerifyEmailModal,
  forceNoRender,
  noModalDisplay,
  userEmail,
  largeText,
}: Props) => {
  const dispatch = useDispatch();
  const [isConfirmedEmail, setIsConfirmedEmail] = useState<boolean>(false);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(SECONDS);
  const [resetCount, setResetCount] = useState<number>(0);

  const resendEmail = useCallback(() => {
    setRemainingSeconds(SECONDS);
    setResetCount(resetCount + 1);
    dispatch(
      enrollAuth({
        requestType: ENROLL_AUTH_REQUEST_TYPE.RESEND_EMAIL,
        email: userEmail,
      }),
    );
  }, [dispatch, userEmail, resetCount]);

  try {
    const channelVerifyEmailModalPage = new BroadcastChannel('confirmedEmail');
    channelVerifyEmailModalPage.addEventListener('message', (event) => {
      setIsConfirmedEmail(event.data);
    });
  } catch {
    console.error('BroadcastChannel is not supported by the browser');
  }

  const counterCallback = useCallback(
    (currentValue) => setRemainingSeconds(currentValue),
    [],
  );

  useCounter(
    counterCallback,
    1000,
    SECONDS,
    `VerifyEmailModal-${resetCount}`,
  );

  if (forceNoRender) {
    return <></>;
  }

  return (
    <div className={styles.wrapperContent}>
      {!noModalDisplay && (
      <h6 id="verifyEmailTitle" aria-label="verify email title">
        {lang.commonOnboardingEmailConfirmTitle()}
      </h6>
      )}
      {!isConfirmedEmail && !noModalDisplay && (
        <ClickableImage
          alt=""
          src={IconClose}
          className={styles.closeIcon}
          onClick={handleCloseVerifyEmailModal}
        />
      )}
      <img src={emailImage} alt="letter" className={styles.image} />
      <div
        className={classNames(styles.text,
          { [styles.largeText]: largeText },
          { [styles.lightText]: noModalDisplay })}
        id="verificationText"
        aria-label="verification text"
      >
        {noModalDisplay && (
          <h6 id="verifyEmailTitle" aria-label="verify email title">
            {lang.commonOnboardingEmailConfirmTitle()}
          </h6>
        )}

        <span className={classNames(styles.text, { [styles.lightText]: noModalDisplay })}>
          <span className={styles.firstText}>{lang.commonOnboardingEmailConfirmDescription1()}</span>
          <span className={styles.userEmailText}>
            {`${userEmail}`}
          </span>
          {'. '}
          {lang.commonOnboardingEmailConfirmDescription2()}
          {'. '}
        </span>
        <p className={classNames(styles.text, { [styles.lightText]: noModalDisplay })}>
          <span className={styles.firstText}>{lang.commonOnboardingEmailSpamDescription1()}</span>
          {'? '}
          <span className={styles.firstText}>{lang.commonOnboardingEmailSpamDescription2()}</span>
        </p>
        <p className={classNames(styles.text, { [styles.lightText]: noModalDisplay })}>
          <span className={styles.firstText}>{lang.commonOr()}</span>
        </p>
      </div>
      {isConfirmedEmail && (
      <p className={classNames(styles.text, { [styles.lightText]: noModalDisplay })}>
        {lang.commonOnboardingEmailAccountHasBeenConfirmed()}
      </p>
      )}
      {
        !isConfirmedEmail && (
        <div className={classNames(styles.text, { [styles.lightText]: noModalDisplay })} aria-label="haven't receive a confirmation email">
          {!noModalDisplay && lang.webSignInEmailNotReceived()}
          {
            remainingSeconds === 0
              ? (
                <ClickableDiv
                  id="sendAgain"
                  className={styles.link}
                  onClick={resendEmail}
                  aria-label="send again"
                >
                  {lang.webSendAgain()}
                </ClickableDiv>
              )
              : (
                <div className={styles.timer}>
                  <div className={styles.sendAgain}>{lang.webResendEmail()}</div>
                  <div className={styles.remainingTime}>{calculateElapsedTime(remainingSeconds, 0)}</div>
                </div>
              )
          }
        </div>
        )
      }
    </div>
  );
};

export default VerifyEmailModal;
